import { z } from 'zod'
import { AggregateProfileSchema } from './aggregate-entity'

// ─── Filter Profile Preset ───

export const FilterProfilePresetSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    filters: z.union([z.array(z.record(z.unknown())), z.record(z.unknown())]),
    /** One-line "what this is / when to use it" shown in the Profile Library
     *  preview. Authored for built-in presets; user profiles omit it. */
    description: z.string().optional(),
    createdAt: z.string().optional(),
    updatedAt: z.string().optional(),
  })
  .passthrough()

export type FilterProfilePreset = z.infer<typeof FilterProfilePresetSchema>

// ─── Default Filter Profiles (Scanner) ───

export const DEFAULT_FILTER_PROFILES: FilterProfilePreset[] = [
  {
    id: 'default_filter_default',
    name: 'Default',
    description: 'Every common stock in the session — the unfiltered starting point.',
    filters: [
      {
        AND: [
          {
            column: 'share_group',
            operator: 'in',
            value: ['CS'],
            rightType: 'value',
            enabled: true,
          },
        ],
      },
    ],
  },
  {
    // The Day Trader layout's scan, leaf for leaf (Chris, 2026-09-24). The scanner
    // pill names a preset only while a panel's filters compile to exactly this, so
    // presets/day-trader.json and this body must not drift; index.unit.test.ts
    // asserts the Day Trader scanner still reads "Small Cap".
    id: 'default_filter_small_cap',
    name: 'Small Cap',
    description:
      'Small-cap momentum: $1–25 common stock under a $1B cap, up 10%+ on 200K+ premarket or 500K+ session volume.',
    filters: [
      {
        AND: [
          {
            column: 'share_group',
            operator: 'in',
            value: ['CS'],
            rightType: 'value',
            enabled: true,
          },
          {
            column: 'marketcap',
            operator: 'lt',
            value: 1_000_000_000,
            rightType: 'value',
            enabled: true,
            allowNull: true,
          },
          {
            column: 'session_lp',
            operator: 'between',
            value: { low: 1, high: 25 },
            rightType: 'value',
            enabled: true,
          },
          {
            OR: [
              {
                column: 'pm_v',
                operator: 'gt',
                value: 200_000,
                rightType: 'value',
                enabled: true,
              },
              {
                column: 'session_v',
                operator: 'gt',
                value: 500_000,
                rightType: 'value',
                enabled: true,
              },
            ],
          },
          {
            column: 'session_chg_p',
            operator: 'gte',
            value: 10,
            rightType: 'value',
            enabled: true,
          },
        ],
      },
    ],
  },
  {
    id: 'default_filter_large_cap',
    name: 'Large Cap',
    description: 'Larger names on the move: $15–100 price, up 5%+ with 300K+ shares traded.',
    filters: [
      {
        AND: [
          {
            column: 'session_lp',
            operator: 'between',
            value: { low: 15, high: 100 },
            rightType: 'value',
            enabled: true,
          },
          {
            column: 'session_chg_p',
            operator: 'gte',
            value: 5,
            rightType: 'value',
            enabled: true,
          },
          {
            column: 'session_v',
            operator: 'gte',
            value: 300_000,
            rightType: 'value',
            enabled: true,
          },
          {
            column: 'share_group',
            operator: 'in',
            value: ['CS'],
            rightType: 'value',
            enabled: true,
          },
        ],
      },
    ],
  },
  {
    // The Swing Trader layout's scan (Chris, 2026-09-24): mid and large names
    // pressing the top of their one-month range.
    id: 'default_filter_swing_setups',
    name: 'Swing Setups',
    description:
      'Common stock at $15–250 trading in the top 10% of its 21-day range, on 300K+ shares.',
    filters: [
      {
        AND: [
          {
            column: 'share_group',
            operator: 'in',
            value: ['CS'],
            rightType: 'value',
            enabled: true,
          },
          {
            column: 'session_lp',
            operator: 'between',
            value: { low: 15, high: 250 },
            rightType: 'value',
            enabled: true,
          },
          {
            column: 'day_21_range_pct',
            operator: 'gt',
            value: 90,
            rightType: 'value',
            enabled: true,
          },
          {
            column: 'session_v',
            operator: 'gte',
            value: 300_000,
            rightType: 'value',
            enabled: true,
          },
        ],
      },
    ],
  },
  {
    id: 'default_filter_afterhours',
    name: 'Afterhours Scanner',
    description: 'After-hours movers: $2–15 stocks up 10%+ in the extended session.',
    filters: [
      {
        AND: [
          {
            column: 'session_lp',
            operator: 'between',
            value: { low: 2, high: 15 },
            rightType: 'value',
            enabled: true,
          },
          {
            column: 'ah_chg_p',
            operator: 'gte',
            value: 10,
            rightType: 'value',
            enabled: true,
          },
          {
            column: 'ah_v',
            operator: 'gte',
            value: 50_000,
            rightType: 'value',
            enabled: true,
          },
          {
            column: 'share_group',
            operator: 'in',
            value: ['CS'],
            rightType: 'value',
            enabled: true,
          },
        ],
      },
    ],
  },
  {
    // Ported verbatim from the legacy app (predef__premarket-gappers, 1,546 users). Cap/float leaves keep allowNull so names with missing fundamentals still scan.
    id: 'default_filter_premarket_gappers',
    name: 'Premarket Gappers',
    description:
      'Small caps ($1–20, under 1B cap, under 100M float) up 30%+ off the PM high or 10%+ on the session, on 50K+ PM volume.',
    filters: [
      {
        AND: [
          {
            column: 'share_group',
            operator: 'in',
            value: ['CS'],
            rightType: 'value',
            enabled: true,
            allowNull: true,
          },
          {
            column: 'marketcap',
            operator: 'lt',
            value: 1_000_000_000,
            rightType: 'value',
            enabled: true,
            allowNull: true,
          },
          {
            column: 'sharesfloat',
            operator: 'lt',
            value: 100_000_000,
            rightType: 'value',
            enabled: true,
            allowNull: true,
          },
          {
            column: 'session_lp',
            operator: 'between',
            value: { low: 1, high: 20 },
            rightType: 'value',
            enabled: true,
          },
          {
            OR: [
              {
                column: 'chg_pm_h_pc_pct',
                operator: 'gte',
                value: 30,
                rightType: 'value',
                enabled: true,
              },
              {
                column: 'session_chg_p',
                operator: 'gte',
                value: 10,
                rightType: 'value',
                enabled: true,
              },
            ],
          },
          { column: 'pm_v', operator: 'gte', value: 50_000, rightType: 'value', enabled: true },
        ],
      },
    ],
  },
  {
    // Legacy predef__small-caps-stocks-in-play ("SC - Stocks in Play").
    id: 'default_filter_stocks_in_play_small_cap',
    name: 'Stocks in Play (Small Cap)',
    description: 'Small caps up 30%+ over three days on 1M+ volume: the multi-day movers.',
    filters: [
      {
        AND: [
          {
            column: 'share_group',
            operator: 'in',
            value: ['CS'],
            rightType: 'value',
            enabled: true,
            allowNull: true,
          },
          {
            column: 'marketcap',
            operator: 'lt',
            value: 1_000_000_000,
            rightType: 'value',
            enabled: true,
            allowNull: true,
          },
          {
            column: 'sharesfloat',
            operator: 'lt',
            value: 100_000_000,
            rightType: 'value',
            enabled: true,
            allowNull: true,
          },
          {
            column: 'session_lp',
            operator: 'between',
            value: { low: 1, high: 20 },
            rightType: 'value',
            enabled: true,
          },
          { column: 'pc_3_chg_p', operator: 'gte', value: 30, rightType: 'value', enabled: true },
          {
            column: 'session_v',
            operator: 'gte',
            value: 1_000_000,
            rightType: 'value',
            enabled: true,
          },
        ],
      },
    ],
  },
  {
    // Legacy predef__mid-large-caps-stocks-in-play ("MC/LC - Stocks in Play").
    id: 'default_filter_stocks_in_play_mid_large_cap',
    name: 'Stocks in Play (Mid/Large Cap)',
    description:
      'Under 10B cap, over $10, ATR14 above 0.5, 5M+ ADTV, gapping 3%+ either way on 3x relative volume.',
    filters: [
      {
        AND: [
          {
            column: 'share_group',
            operator: 'in',
            value: ['CS'],
            rightType: 'value',
            enabled: true,
            allowNull: true,
          },
          {
            column: 'marketcap',
            operator: 'lt',
            value: 10_000_000_000,
            rightType: 'value',
            enabled: true,
            allowNull: true,
          },
          { column: 'session_lp', operator: 'gt', value: 10, rightType: 'value', enabled: true },
          {
            column: 'day_m1_atr_14',
            operator: 'gt',
            value: 0.5,
            rightType: 'value',
            enabled: true,
          },
          {
            column: 'day_m1_adtv_14',
            operator: 'gte',
            value: 5_000_000,
            rightType: 'value',
            enabled: true,
          },
          {
            OR: [
              { column: 'pm_chg_p', operator: 'gte', value: 3, rightType: 'value', enabled: true },
              { column: 'pm_chg_p', operator: 'lte', value: -3, rightType: 'value', enabled: true },
            ],
          },
          { column: 'rvol', operator: 'gte', value: 3, rightType: 'value', enabled: true },
        ],
      },
    ],
  },
  {
    // Legacy predef__day-2-plays (scanner sense: yesterday's runner, today's continuation).
    id: 'default_filter_day_2_plays',
    name: 'Day 2 Plays',
    description:
      'Small caps that ran 30%+ yesterday on 2M+ volume and are still trading 500K+ today.',
    filters: [
      {
        AND: [
          {
            column: 'share_group',
            operator: 'in',
            value: ['CS'],
            rightType: 'value',
            enabled: true,
            allowNull: true,
          },
          {
            column: 'marketcap',
            operator: 'lt',
            value: 1_000_000_000,
            rightType: 'value',
            enabled: true,
            allowNull: true,
          },
          {
            column: 'sharesfloat',
            operator: 'lt',
            value: 100_000_000,
            rightType: 'value',
            enabled: true,
            allowNull: true,
          },
          {
            column: 'session_lp',
            operator: 'between',
            value: { low: 1, high: 20 },
            rightType: 'value',
            enabled: true,
          },
          {
            column: 'day_m1_chg_pct',
            operator: 'gte',
            value: 30,
            rightType: 'value',
            enabled: true,
          },
          {
            column: 'day_m1_vol',
            operator: 'gte',
            value: 2_000_000,
            rightType: 'value',
            enabled: true,
          },
          {
            column: 'session_v',
            operator: 'gte',
            value: 500_000,
            rightType: 'value',
            enabled: true,
          },
        ],
      },
    ],
  },
  {
    // Legacy predef__midday-runners.
    id: 'default_filter_midday_runners',
    name: 'Midday Runners',
    description:
      'Small caps that did not gap (PM gap under 5%) but are up 10%+ from the open on 1M+ volume.',
    filters: [
      {
        AND: [
          {
            column: 'share_group',
            operator: 'in',
            value: ['CS'],
            rightType: 'value',
            enabled: true,
            allowNull: true,
          },
          {
            column: 'marketcap',
            operator: 'lt',
            value: 1_000_000_000,
            rightType: 'value',
            enabled: true,
            allowNull: true,
          },
          {
            column: 'sharesfloat',
            operator: 'lt',
            value: 100_000_000,
            rightType: 'value',
            enabled: true,
            allowNull: true,
          },
          {
            column: 'session_lp',
            operator: 'between',
            value: { low: 1, high: 20 },
            rightType: 'value',
            enabled: true,
          },
          { column: 'pm_chg_p', operator: 'lt', value: 5, rightType: 'value', enabled: true },
          { column: 'o_chg_p', operator: 'gte', value: 10, rightType: 'value', enabled: true },
          {
            column: 'session_v',
            operator: 'gte',
            value: 1_000_000,
            rightType: 'value',
            enabled: true,
          },
        ],
      },
    ],
  },
]

// ─── Default Filter Profiles (History) ───

/** Id of the built-in history filter profile used as the default seed (Recent Gappers). */
export const DEFAULT_HISTORY_FILTER_PROFILE_ID = 'default_history_recent_gappers'

/**
 * History filter preset ids retired by the 2026-09-12 merge (Chris: "a lot of
 * these are duplicated"). Persisted layouts and the migration mapper may still
 * carry them; resolve through here before looking a preset up by id.
 */
export const HISTORY_FILTER_PROFILE_ID_ALIASES: Record<string, string> = {
  default_history_premarket_gappers: 'default_history_recent_gappers',
  default_history_intraday_runners: 'default_history_intraday_movers',
  default_history_large_cap_movers: 'default_history_large_cap',
}

export function resolveHistoryFilterProfileId(id: string): string {
  return HISTORY_FILTER_PROFILE_ID_ALIASES[id] ?? id
}

export const DEFAULT_HISTORY_FILTER_PROFILES: FilterProfilePreset[] = [
  {
    // The default seed. Legacy History page Recent Gappers, verbatim (predef__recent-gappers, 180 users).
    // Merged 2026-09-12 (Chris): the dashboard Premarket Gappers (gap 20, 1M volume, open 2..15) folds into this.
    // No date leaf: the page injects its sticky rolling window (legacy scoped 90 days).
    id: 'default_history_recent_gappers',
    name: 'Recent Gappers',
    description: 'Gapped up 20%+ into the open on 2M+ volume, priced $1 to 15, common stock only.',
    filters: [
      {
        AND: [
          {
            column: 'share_group',
            operator: 'in',
            value: ['CS'],
            rightType: 'value',
            enabled: true,
          },
          {
            column: 'day0_open',
            operator: 'between',
            value: { low: 1, high: 15 },
            rightType: 'value',
            enabled: true,
          },
          { column: 'day0_gap_pct', operator: 'gte', value: 20, rightType: 'value', enabled: true },
          {
            column: 'day0_vol',
            operator: 'gte',
            value: 2_000_000,
            rightType: 'value',
            enabled: true,
          },
        ],
      },
    ],
  },
  {
    // Ported from the legacy History page (predef__high-gappers). No date leaf: the page injects its sticky rolling window.
    id: 'default_history_high_gappers',
    name: 'High Gappers',
    description: 'Gapped up 50%+ into the open on 2M+ volume, priced $1–15.',
    filters: [
      {
        AND: [
          {
            column: 'share_group',
            operator: 'in',
            value: ['CS'],
            rightType: 'value',
            enabled: true,
          },
          {
            column: 'day0_open',
            operator: 'between',
            value: { low: 1, high: 15 },
            rightType: 'value',
            enabled: true,
          },
          { column: 'day0_gap_pct', operator: 'gte', value: 50, rightType: 'value', enabled: true },
          {
            column: 'day0_vol',
            operator: 'gte',
            value: 2_000_000,
            rightType: 'value',
            enabled: true,
          },
        ],
      },
    ],
  },
  {
    // Legacy predef__intraday-movers. Merged 2026-09-12 (Chris): the dashboard Intraday Runners
    // (closed 15%+ above the open on 5M volume) folds into this body.
    id: 'default_history_intraday_movers',
    name: 'Intraday Movers',
    description: 'No gap (3% or less) but ran 50%+ from the open on 2M+ volume, priced $1–15.',
    filters: [
      {
        AND: [
          {
            column: 'share_group',
            operator: 'in',
            value: ['CS'],
            rightType: 'value',
            enabled: true,
          },
          {
            column: 'day0_open',
            operator: 'between',
            value: { low: 1, high: 15 },
            rightType: 'value',
            enabled: true,
          },
          { column: 'day0_gap_pct', operator: 'lte', value: 3, rightType: 'value', enabled: true },
          {
            column: 'day0_open_high_pct',
            operator: 'gte',
            value: 50,
            rightType: 'value',
            enabled: true,
          },
          {
            column: 'day0_vol',
            operator: 'gte',
            value: 2_000_000,
            rightType: 'value',
            enabled: true,
          },
        ],
      },
    ],
  },
  {
    id: 'default_history_morning_runners',
    name: 'Morning Runners',
    description: 'Up 15%+ into the 11AM (90-minute) high on 1M+ 90-minute volume.',
    filters: [
      {
        AND: [
          {
            column: 'day0_90min_high_pct',
            operator: 'gte',
            value: 15,
            rightType: 'value',
            enabled: true,
          },
          {
            column: 'day0_90min_vol',
            operator: 'gte',
            value: 1_000_000,
            rightType: 'value',
            enabled: true,
          },
        ],
      },
    ],
  },
  {
    // Legacy History-page predef__day-2-plays (prior-day study; the scanner's Day 2 Plays is a live session filter).
    id: 'default_history_day_2_plays',
    name: 'Day 2 Plays',
    description:
      'The day after a 40%+ gap and 20%+ close on 2M+ volume: what the continuation day did, priced $1–15 on 1M+ volume.',
    filters: [
      {
        AND: [
          {
            column: 'share_group',
            operator: 'in',
            value: ['CS'],
            rightType: 'value',
            enabled: true,
          },
          {
            column: 'day_m1_chg_pct',
            operator: 'gte',
            value: 20,
            rightType: 'value',
            enabled: true,
          },
          {
            column: 'day_m1_gap_pct',
            operator: 'gte',
            value: 40,
            rightType: 'value',
            enabled: true,
          },
          {
            column: 'day_m1_vol',
            operator: 'gte',
            value: 2_000_000,
            rightType: 'value',
            enabled: true,
          },
          {
            column: 'day0_open',
            operator: 'between',
            value: { low: 1, high: 15 },
            rightType: 'value',
            enabled: true,
          },
          {
            column: 'day0_vol',
            operator: 'gte',
            value: 1_000_000,
            rightType: 'value',
            enabled: true,
          },
        ],
      },
    ],
  },
  {
    // Merged 2026-09-12 (Chris): the dashboard "Large Cap" (up 5% on 1M volume, no cap leaf) folded into
    // the History page Large Cap Movers body, which is the one that actually says large cap.
    // leaf; dropped 2026-09-11 (Chris): a null change % means no prior close,
    // not an unknown that should pass an "up 5%+" scan.
    id: 'default_history_large_cap',
    name: 'Large Cap',
    description: '10B+ market cap, $25–500, closed up 5%+ on 2M+ volume.',
    filters: [
      {
        AND: [
          {
            column: 'share_group',
            operator: 'in',
            value: ['CS'],
            rightType: 'value',
            enabled: true,
          },
          {
            column: 'marketcap',
            operator: 'gte',
            value: 10_000_000_000,
            rightType: 'value',
            enabled: true,
            allowNull: true,
          },
          {
            column: 'day0_open',
            operator: 'between',
            value: { low: 25, high: 500 },
            rightType: 'value',
            enabled: true,
          },
          {
            column: 'day0_vol',
            operator: 'gte',
            value: 2_000_000,
            rightType: 'value',
            enabled: true,
          },
          {
            column: 'day0_chg_pct',
            operator: 'gte',
            value: 5,
            rightType: 'value',
            enabled: true,
          },
        ],
      },
    ],
  },
  {
    // Legacy predef__stocks-with-halts; the only halt-based preset in either app.
    id: 'default_history_stocks_with_halts',
    name: 'Stocks with Halts',
    description: 'Sessions with at least one trading halt, priced $1–15 on 2M+ volume.',
    filters: [
      {
        AND: [
          {
            column: 'share_group',
            operator: 'in',
            value: ['CS'],
            rightType: 'value',
            enabled: true,
          },
          {
            column: 'day0_open',
            operator: 'between',
            value: { low: 1, high: 15 },
            rightType: 'value',
            enabled: true,
          },
          {
            column: 'day0_vol',
            operator: 'gte',
            value: 2_000_000,
            rightType: 'value',
            enabled: true,
          },
          { column: 'halts_count', operator: 'gte', value: 1, rightType: 'value', enabled: true },
        ],
      },
    ],
  },
]

// ─── Default Filter Profiles (Ticker Stats) ───
//
// Ticker Stats filters one ticker's own history, so its profiles live in their own
// collection (`tickerStats` / `filterProfiles`), apart from the History page's: stock-level
// traits like float, market cap and share class barely vary for a single ticker, and a save
// on one list must never reach the other. Chris 2026-09-27: named by threshold, gap and
// change (close vs prior close), each on more than 1M shares; no gap-down versions. Recent
// Gappers is not a preset here (a user's saved profile, if anyone wants it).

/**
 * Id of the Ticker Stats default (a panel with no saved filters): Gap 3%+, the widest net
 * (Chris 2026-09-27). Recent Gappers was the default until then; it is a saved profile of his
 * own now, not a preset.
 */
export const DEFAULT_TICKER_STATS_FILTER_PROFILE_ID = 'default_ts_filter_gap_3'

export const DEFAULT_TICKER_STATS_FILTER_PROFILES: FilterProfilePreset[] = [
  {
    id: 'default_ts_filter_gap_3',
    name: 'Gap 3%+',
    description: 'Days the ticker gapped up 3%+ into the open on more than 1M shares.',
    filters: [
      {
        AND: [
          { column: 'day0_gap_pct', operator: 'gte', value: 3, rightType: 'value', enabled: true },
          {
            column: 'day0_vol',
            operator: 'gt',
            value: 1_000_000,
            rightType: 'value',
            enabled: true,
          },
        ],
      },
    ],
  },
  {
    id: 'default_ts_filter_gap_20',
    name: 'Gap 20%+',
    description: 'Days the ticker gapped up 20%+ into the open on more than 1M shares.',
    filters: [
      {
        AND: [
          { column: 'day0_gap_pct', operator: 'gte', value: 20, rightType: 'value', enabled: true },
          {
            column: 'day0_vol',
            operator: 'gt',
            value: 1_000_000,
            rightType: 'value',
            enabled: true,
          },
        ],
      },
    ],
  },
  {
    id: 'default_ts_filter_gap_50',
    name: 'Gap 50%+',
    description: 'Days the ticker gapped up 50%+ into the open on more than 1M shares.',
    filters: [
      {
        AND: [
          { column: 'day0_gap_pct', operator: 'gte', value: 50, rightType: 'value', enabled: true },
          {
            column: 'day0_vol',
            operator: 'gt',
            value: 1_000_000,
            rightType: 'value',
            enabled: true,
          },
        ],
      },
    ],
  },
  {
    id: 'default_ts_filter_change_3',
    name: 'Change 3%+',
    description: 'Days the ticker closed 3%+ above the prior close on more than 1M shares.',
    filters: [
      {
        AND: [
          { column: 'day0_chg_pct', operator: 'gte', value: 3, rightType: 'value', enabled: true },
          {
            column: 'day0_vol',
            operator: 'gt',
            value: 1_000_000,
            rightType: 'value',
            enabled: true,
          },
        ],
      },
    ],
  },
  {
    id: 'default_ts_filter_change_20',
    name: 'Change 20%+',
    description: 'Days the ticker closed 20%+ above the prior close on more than 1M shares.',
    filters: [
      {
        AND: [
          { column: 'day0_chg_pct', operator: 'gte', value: 20, rightType: 'value', enabled: true },
          {
            column: 'day0_vol',
            operator: 'gt',
            value: 1_000_000,
            rightType: 'value',
            enabled: true,
          },
        ],
      },
    ],
  },
  {
    id: 'default_ts_filter_change_50',
    name: 'Change 50%+',
    description: 'Days the ticker closed 50%+ above the prior close on more than 1M shares.',
    filters: [
      {
        AND: [
          { column: 'day0_chg_pct', operator: 'gte', value: 50, rightType: 'value', enabled: true },
          {
            column: 'day0_vol',
            operator: 'gt',
            value: 1_000_000,
            rightType: 'value',
            enabled: true,
          },
        ],
      },
    ],
  },
  {
    // Intraday runners (Chris 2026-09-27): the move after the open, close vs open.
    id: 'default_ts_filter_change_open_15',
    name: 'Change From Open 15%+',
    description: 'Days the ticker closed 15%+ above its open on more than 1M shares.',
    filters: [
      {
        AND: [
          {
            column: 'day0_ret_open_pct',
            operator: 'gte',
            value: 15,
            rightType: 'value',
            enabled: true,
          },
          {
            column: 'day0_vol',
            operator: 'gt',
            value: 1_000_000,
            rightType: 'value',
            enabled: true,
          },
        ],
      },
    ],
  },
  {
    // Intraday runners, the run itself (Chris 2026-09-27): high vs open, so a day that ran and
    // then faded still counts. The same column the History "Intraday Movers" preset uses.
    id: 'default_ts_filter_high_open_15',
    name: 'High From Open 15%+',
    description: 'Days the ticker ran 15%+ above its open at the high, on more than 1M shares.',
    filters: [
      {
        AND: [
          {
            column: 'day0_open_high_pct',
            operator: 'gte',
            value: 15,
            rightType: 'value',
            enabled: true,
          },
          {
            column: 'day0_vol',
            operator: 'gt',
            value: 1_000_000,
            rightType: 'value',
            enabled: true,
          },
        ],
      },
    ],
  },
]

// ─── Default Filter Profiles (Alert Windows) ───
//
// Alert windows keep their own collection (`alertWindows` / `filterProfiles`) over the
// scanner's columns, so a save on one list never reaches the other.

export const DEFAULT_ALERT_WINDOW_FILTER_PROFILE_ID = 'default_aw_filter_liquid_stocks'

export const DEFAULT_ALERT_WINDOW_FILTER_PROFILES: FilterProfilePreset[] = [
  {
    // The default window's filters (spec 4.3): no change filter, so new lows fire as readily
    // as new highs. In pre-market, session_v reads pre-market volume.
    id: 'default_aw_filter_liquid_stocks',
    name: 'Liquid Stocks',
    description: 'Last price $1 and up with 500K+ shares traded today, up or down.',
    filters: [
      {
        AND: [
          { column: 'session_lp', operator: 'gte', value: 1, rightType: 'value', enabled: true },
          {
            column: 'session_v',
            operator: 'gte',
            value: 500_000,
            rightType: 'value',
            enabled: true,
          },
        ],
      },
    ],
  },
  {
    id: 'default_aw_filter_active_movers',
    name: 'Active Movers',
    description: 'Stocks over $1 up 5%+ today with 500K+ shares traded.',
    filters: [
      {
        AND: [
          { column: 'session_lp', operator: 'gte', value: 1, rightType: 'value', enabled: true },
          { column: 'session_chg_p', operator: 'gte', value: 5, rightType: 'value', enabled: true },
          {
            column: 'session_v',
            operator: 'gte',
            value: 500_000,
            rightType: 'value',
            enabled: true,
          },
        ],
      },
    ],
  },
]

// ─── Color Rule ───

// One point of a multi-stop gradient. `value` pins an interior stop to a data
// value (e.g. 0 = neutral); omitted, it is spaced evenly between its pinned
// neighbours. The first and last stops sit at the rule's min/max, so their
// `value` is never read.
export const ColorStopSchema = z.object({
  color: z.string(),
  value: z.number().optional(),
})

export type ColorStop = z.infer<typeof ColorStopSchema>

// Passthrough so fields added later survive a parse + re-save by a client
// running an older build instead of being stripped.
//
// `stops` is written only when the gradient can't be expressed as
// start/[mid]/end (4+ colours, or a pinned interior value). start/mid/end are
// always kept as its three-point approximation, so a reader that ignores
// `stops` still draws a sensible gradient. See components/grid/color-stops.ts.
export const ColorRuleSchema = z
  .object({
    column: z.string(),
    startColor: z.string(),
    endColor: z.string(),
    midColor: z.string().optional(),
    minValue: z.number().optional(),
    maxValue: z.number().optional(),
    stops: z.array(ColorStopSchema).optional(),
  })
  .passthrough()

export type ColorRule = z.infer<typeof ColorRuleSchema>

// ─── Display Mode ───
//
// Per-column render style in the grid. Stored as a sparse map keyed by column
// key (absent key ⇒ 'number'), mirroring how colorRules attach per column.
// v1: number | bar | bullet. Extensible (e.g. 'sparkline') without a migration.

// The STORED modes. A panel whose state fails its Zod parse is dropped whole by an
// older tab, so a new style is never added here: it is stored as an existing mode
// plus an optional flag the old `z.object` strips (see `timeOfDay` below).
export const DisplayModeSchema = z.enum(['number', 'bar', 'bullet'])

export type StoredDisplayMode = z.infer<typeof DisplayModeSchema>

/** In-memory style: the stored modes plus those stored as a flag on `number`. */
export type DisplayMode = StoredDisplayMode | 'timeofday'

// Per-column display config. `mode` is the visual style; `showValue` toggles the
// formatted value label alongside a bar/bullet (default true; irrelevant for
// 'number', which IS the value); `background` renders a `bar` as a fill behind
// the value instead of an inline track beside it (bar-only, default false).
// Stored canonically — optional flags omitted at their defaults, so a plain bar
// is just `{ mode: 'bar' }`.
export const ColumnDisplaySchema = z.object({
  // An unknown mode (written by a newer build) degrades to a plain number rather
  // than failing the whole panel state.
  mode: DisplayModeSchema.catch('number'),
  showValue: z.boolean().optional(),
  background: z.boolean().optional(),
  // Force green/red sign coloring on a `bar` for a semantically signed metric
  // (e.g. change %), so an all-positive column keeps its sign color instead of
  // falling back to the neutral fill. Bar-only; auto-detected when omitted.
  signed: z.boolean().optional(),
  // Stored form of the "Time of day" style (high/low time columns): `mode` stays
  // 'number' so a tab on the previous build, which strips this key, reads plain text.
  timeOfDay: z.boolean().optional(),
})

export type StoredColumnDisplay = z.infer<typeof ColumnDisplaySchema>

export const ColumnDisplayModesSchema = z.record(z.string(), ColumnDisplaySchema)

export type ColumnDisplayModes = z.infer<typeof ColumnDisplayModesSchema>

/** In-memory display config: what the picker, labels and renderers switch on. */
export type ColumnDisplay = Omit<StoredColumnDisplay, 'mode' | 'timeOfDay'> & {
  mode: DisplayMode
}

/** Stored to in-memory (normalize on read): `{ mode: 'number', timeOfDay }` is Time of day. */
export function effectiveDisplayMode(stored: StoredColumnDisplay | undefined): ColumnDisplay {
  if (!stored) return { mode: 'number' }
  const { timeOfDay, ...rest } = stored
  return timeOfDay === true && stored.mode === 'number' ? { ...rest, mode: 'timeofday' } : rest
}

/** In-memory to stored (denormalize on write). */
export function storedColumnDisplay(display: ColumnDisplay): StoredColumnDisplay {
  if (display.mode === 'timeofday') return { ...display, mode: 'number', timeOfDay: true }
  return display as StoredColumnDisplay
}

// ─── Column Profile Preset ───

export const ColumnProfilePresetSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    columns: z.array(z.string()),
    colorRules: z.array(ColorRuleSchema).optional(),
    columnDisplayModes: ColumnDisplayModesSchema.optional(),
    createdAt: z.string().optional(),
    updatedAt: z.string().optional(),
  })
  .passthrough()

export type ColumnProfilePreset = z.infer<typeof ColumnProfilePresetSchema>

// ─── Default Column Profiles (Scanner) ───

export const DEFAULT_SCANNER_COLUMN_PROFILES: ColumnProfilePreset[] = [
  {
    id: 'default_col_default',
    name: 'Default',
    columns: [
      'ticker',
      'total_news_count',
      'session_lp',
      'session_v',
      'session_chg_p',
      'day_1_range_pct',
      'sharesfloat',
      'marketcap',
    ],
    columnDisplayModes: {
      session_chg_p: { mode: 'bar' },
      day_1_range_pct: { mode: 'bullet' },
    },
  },
  {
    id: 'default_col_premarket',
    name: 'Premarket',
    columns: [
      'ticker',
      'total_news_count',
      'session_lp',
      'pm_v',
      'pm_chg_p',
      'day_1_range_pct',
      'sharesfloat',
      'marketcap',
    ],
    columnDisplayModes: {
      pm_chg_p: { mode: 'bar' },
      day_1_range_pct: { mode: 'bullet' },
    },
  },
  {
    id: 'default_col_after_hours',
    name: 'After Hours',
    columns: [
      'ticker',
      'total_news_count',
      'session_lp',
      'ah_v',
      'ah_chg_p',
      'day_1_range_pct',
      'sharesfloat',
      'marketcap',
    ],
    columnDisplayModes: {
      ah_chg_p: { mode: 'bar' },
      day_1_range_pct: { mode: 'bullet' },
    },
  },
]

// ─── Default Column Profiles (History) ───
//
// History columns are a different catalog from the scanner's (day0_*/dayN_*
// event-relative keys, not session_*), so the scanner defaults can't be
// reused here: history/columnProfiles simply shipped with no defaults at
// all, leaving the Column profile dropdown empty on both the History page
// and the Exports wizard. Ids are `default_hist_col_*` so they can never
// collide with `default_col_*` (scanner/watchlist) even though both live
// under the `columnProfiles` type.
//
// Every key below is asserted against HISTORY_COLUMN_MAP by
// profile-catalog-integrity.unit.test.ts. When adding a preset here, also
// add its id to apps/edge-user-api/edge_user_api/services/default_profile_ids.py
// under ("history", "columnProfiles"). The save endpoint 400s on any
// default id that reaches persistence, and that registry is the gate.

export const DEFAULT_HISTORY_COLUMN_PROFILES: ColumnProfilePreset[] = [
  {
    // Mirrors DEFAULT_HISTORY_RECORDS_COLUMNS, the columns the grid already
    // starts with, so "Default" is a true reset rather than a fourth shape.
    // Reshaped 2026-09-11 (Chris) to the legacy "General" body (1,468
    // dashboard + 207 History users) so the intraday timing columns are
    // there from the first open.
    id: 'default_hist_col_default',
    name: 'Default',
    columns: [
      'ticker',
      'day0_date',
      'day0_open',
      'day0_pm_vol',
      'day0_gap_pct',
      'day0_chg_pct',
      'day0_vol',
      'day0_open_high_pct',
      'day0_open_low_pct',
      'day0_ret_open_pct',
      'day0_high_time',
      'day0_low_time',
    ],
    columnDisplayModes: {
      day0_gap_pct: { mode: 'bar' },
      day0_chg_pct: { mode: 'bar' },
      day0_ret_open_pct: { mode: 'bar' },
      day0_high_time: { mode: 'number', timeOfDay: true },
      day0_low_time: { mode: 'number', timeOfDay: true },
    },
  },
  {
    // The gap-day anatomy: where it opened relative to the gap, how far it
    // ran, and where it closed in the day's range.
    id: 'default_hist_col_gap_day',
    name: 'Gap Day',
    columns: [
      'ticker',
      'day0_date',
      'day0_gap_pct',
      'day0_open',
      'day0_high',
      'day0_low',
      'day0_close',
      'day0_vol',
      'day0_ret_open_pct',
      'day0_high_low_pct',
    ],
    columnDisplayModes: {
      day0_gap_pct: { mode: 'bar' },
      day0_ret_open_pct: { mode: 'bar' },
    },
  },
  {
    // Day-2/3/4 follow-through: the continuation question, with each day's
    // volume alongside so a fade on dying volume reads at a glance.
    id: 'default_hist_col_follow_through',
    name: 'Follow Through',
    columns: [
      'ticker',
      'day0_date',
      'day0_chg_pct',
      'day1_chg_pct',
      'day2_chg_pct',
      'day3_chg_pct',
      'day0_vol',
      'day1_vol',
      'day2_vol',
    ],
    columnDisplayModes: {
      day0_chg_pct: { mode: 'bar' },
      day1_chg_pct: { mode: 'bar' },
      day2_chg_pct: { mode: 'bar' },
      day3_chg_pct: { mode: 'bar' },
    },
  },
  {
    // Float / market-cap context for the same event rows.
    id: 'default_hist_col_share_structure',
    name: 'Share Structure',
    columns: [
      'ticker',
      'day0_date',
      'day0_close',
      'day0_chg_pct',
      'day0_vol',
      'sharesfloat',
      'sharesoutstanding',
      'marketcap',
      'percentinsiders',
      'percentinstitutions',
    ],
    columnDisplayModes: {
      day0_chg_pct: { mode: 'bar' },
    },
  },
]

export const DEFAULT_TICKER_STATS_COLUMN_PROFILE_ID = 'default_ts_col_general'

// Dashboard Ticker Stats keeps its own column presets: the legacy dashboard
// `historyColumnProfiles` predefs (General, Gappers), which never mixed with the
// History page's. Same catalog and pool as the history presets, distinct ids.
export const DEFAULT_TICKER_STATS_COLUMN_PROFILES: ColumnProfilePreset[] = [
  {
    id: 'default_ts_col_general',
    name: 'General',
    columns: [
      'day0_date',
      'day0_open',
      'day0_gap_pct',
      'day0_chg_pct',
      'day0_vol',
      'day0_high_time',
      'day0_low_time',
    ],
  },
  {
    id: 'default_ts_col_gappers',
    name: 'Gappers',
    columns: [
      'day0_date',
      'day0_open',
      'day0_vol',
      'day0_pm_high_pct',
      'day0_gap_pct',
      'day0_open_high_pct',
      'day0_high_time',
      'day0_low_time',
    ],
  },
]

// Dashboard News column presets: the legacy `newsColumnProfiles` predefs. The legacy
// `combinedHeadline` column is the grid's native headline here, so it is not a column.
export const DEFAULT_NEWS_COLUMN_PROFILES: ColumnProfilePreset[] = [
  {
    id: 'default_news_col_default',
    name: 'Default',
    columns: ['ticker'],
  },
  {
    id: 'default_news_col_performance',
    name: 'Performance',
    columns: ['ticker', 'session_lp', 'session_v', 'session_chg_p'],
  },
  {
    id: 'default_news_col_fundamentals',
    name: 'Fundamentals',
    columns: ['ticker', 'session_lp', 'marketcap', 'sharesfloat'],
  },
]

// ─── Key Stats Group Profile Preset ───

export const KeyStatsGroupProfilePresetSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    groups: z.array(
      z.object({
        label: z.string(),
        columns: z.array(z.string()),
      }),
    ),
    /** Per-stat tint bands (the legacy "alarm" thresholds), applied with the profile. */
    colorRules: z.array(ColorRuleSchema).optional(),
    createdAt: z.string().optional(),
    updatedAt: z.string().optional(),
  })
  .passthrough()

export type KeyStatsGroupProfilePreset = z.infer<typeof KeyStatsGroupProfilePresetSchema>

// Legacy key-stats "legend" bands, ported 2026-09-11 (Chris: yes on the alarm
// colors for the defaults). Legacy carried {less, greater, reversed}; read as
// a red-to-green band from `less` to `greater`, flipped when `reversed` (so a
// small-cap profile paints a 500M+ market cap red, a 10+ current ratio green).
const BAND_RED = '#ef4444'
const BAND_GREEN = '#22c55e'
function band(column: string, low: number, high: number, higherIsBetter: boolean): ColorRule {
  return {
    column,
    startColor: higherIsBetter ? BAND_RED : BAND_GREEN,
    endColor: higherIsBetter ? BAND_GREEN : BAND_RED,
    minValue: low,
    maxValue: high,
  }
}

// ─── Default Key Stats Group Profiles ───
// Ported from the old app's `predefinedLayoutKeystatsProfiles`
// (redux/layoutV2/toplist/defaultProfiles.js:867-1050). The old "Small Cap" /
// "Large Cap" presets used a 2-section × 2-column layout with per-stat alarm
// thresholds; each old section is collapsed to one ordered group, and the alarm
// thresholds are carried as `colorRules` (restored 2026-09-11, see `band`). Column keys are remapped to the new catalog (marketCap →
// marketcap, SSR → ssr, rvolCalculated → rvol, day365_*_combined → day_252_*,
// changeYTDCombined → pc_ytd_chg_p, next_earnings → earnings_date, TotalRevenue
// → revenue, pe_ratio → peratio, home_country → country). WorkingCapital /
// MonthsOfCash / BurnMo / GrossProfit / RetainedEarnings are the client-computed
// derived financials (see key-stats-derived-financials.ts). The old
// `lastSplitCombined` ("date / ratio") stat is two catalog columns here:
// `split_date` + `split_ratio`.
export const DEFAULT_KEY_STATS_GROUP_PROFILES: KeyStatsGroupProfilePreset[] = [
  {
    id: 'default_key_stats_small_cap',
    name: 'Small Cap',
    groups: [
      {
        label: 'Stats',
        columns: [
          'marketcap',
          'sharesoutstanding',
          'sharesfloat',
          'shortpercentfloat',
          'percentinstitutions',
          'percentinsiders',
          'floatrotation',
          'ssr',
          'rvol',
          'day_252_high',
          'day_252_low',
          'pc_ytd_chg_p',
          'earnings_date',
          'split_date',
          'split_ratio',
        ],
      },
      {
        label: 'Financials',
        columns: [
          'cashandcashequivalents',
          'monthsofcash',
          'workingcapital',
          'revenue',
          'currentratio',
          'burnmo',
        ],
      },
    ],
    colorRules: [
      band('marketcap', 150_000_000, 500_000_000, false),
      band('sharesoutstanding', 3_000_000, 10_000_000, true),
      band('sharesfloat', 3_000_000, 10_000_000, true),
      band('shortpercentfloat', 10, 30, false),
      band('percentinstitutions', 10, 30, false),
      band('percentinsiders', 20, 50, false),
      band('floatrotation', 3, 10, false),
      band('monthsofcash', 9, 24, true),
      band('workingcapital', 5_000_000, 25_000_000, true),
      band('currentratio', 2, 10, true),
    ],
  },
  {
    id: 'default_key_stats_large_cap',
    name: 'Large Cap',
    groups: [
      {
        label: 'Stats',
        columns: [
          'marketcap',
          'sharesoutstanding',
          'sharesfloat',
          'shortpercentfloat',
          'percentinstitutions',
          'percentinsiders',
          'country',
          'ssr',
          'rvol',
          'day_252_high',
          'day_252_low',
          'pc_ytd_chg_p',
          'earnings_date',
          'split_date',
          'split_ratio',
        ],
      },
      {
        label: 'Financials',
        columns: [
          'cashandcashequivalents',
          'revenue',
          'peratio',
          'currentratio',
          'grossprofit',
          'retainedearnings',
        ],
      },
    ],
    colorRules: [
      // Legacy had `greater: 10` here, an evident typo for 10B on a large-cap profile.
      band('marketcap', 500_000_000, 10_000_000_000, true),
      band('sharesoutstanding', 100_000_000, 500_000_000, true),
      band('sharesfloat', 100_000_000, 500_000_000, true),
      band('shortpercentfloat', 0, 30, false),
      band('percentinstitutions', 0, 30, false),
      band('percentinsiders', 0, 50, false),
    ],
  },
]

// ─── Aggregate Profile Preset ───

export const AggregateProfilePresetSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    aggregateProfile: AggregateProfileSchema,
    createdAt: z.string().optional(),
    updatedAt: z.string().optional(),
  })
  .passthrough()

export type AggregateProfilePreset = z.infer<typeof AggregateProfilePresetSchema>

// ─── Default Aggregate Profiles ───
// Ported from the old app's `predefinedLayoutHistoryAggregateProfiles`
// (redux/layoutV2/toplist/defaultProfiles.js). Group `data` arrays reference
// aggregate `id`s in display order — see aggregate-form-panel.tsx.
/** Id of the built-in aggregate profile shown by default (General Stats). */
export const DEFAULT_AGGREGATE_PROFILE_ID = 'default_agg_general_stats'

export const DEFAULT_AGGREGATE_PROFILES: AggregateProfilePreset[] = [
  {
    id: 'default_agg_general_stats',
    name: 'General Stats',
    aggregateProfile: {
      name: 'General Stats',
      aggregates: [
        {
          id: 'gs_d1_count',
          conditionalAgg: 'CNT',
          label: 'Count',
          description: 'Number of sessions in the result set',
          target: { column: 'day0_close', type: 'column' },
          conditions: [],
          chartType: 'bar',
        },
        {
          id: 'gs_d1_gap',
          conditionalAgg: 'AVG',
          label: 'Gap',
          description: 'Average price difference from prev close to open',
          target: { column: 'day0_gap', type: 'column' },
          conditions: [],
          chartType: 'line',
        },
        {
          id: 'gs_d1_gap_pct',
          conditionalAgg: 'AVG',
          label: 'Gap %',
          description: 'Average percentage gap',
          target: { column: 'day0_gap_pct', type: 'column' },
          conditions: [],
          chartType: 'line',
        },
        {
          id: 'gs_d1_close_gt_open',
          conditionalAgg: 'CNT',
          label: 'Close > Open',
          description: 'Sessions that closed above open',
          target: { column: 'day0_close', type: 'column' },
          conditions: [
            {
              left: { column: 'day0_ret_open' },
              operator: 'gte',
              right: 0,
              rightType: 'value',
              enabled: true,
            },
          ],
          chartType: 'bar',
        },
        {
          id: 'gs_d1_close_lt_open',
          conditionalAgg: 'CNT',
          label: 'Close < Open',
          description: 'Sessions that closed below open',
          target: { column: 'day0_close', type: 'column' },
          conditions: [
            {
              left: { column: 'day0_ret_open' },
              operator: 'lt',
              right: 0,
              rightType: 'value',
              enabled: true,
            },
          ],
          chartType: 'bar',
        },
        {
          id: 'gs_d1_hod',
          conditionalAgg: 'AVG',
          label: 'HOD',
          description: 'Average time of the high of day',
          target: { column: 'day0_high_time', type: 'column' },
          conditions: [],
          chartType: 'line',
        },
        {
          id: 'gs_d1_lod',
          conditionalAgg: 'AVG',
          label: 'LOD',
          description: 'Average time of the low of day',
          target: { column: 'day0_low_time', type: 'column' },
          conditions: [],
          chartType: 'line',
        },
        {
          id: 'gs_d1_high_spike_pct',
          conditionalAgg: 'AVG',
          label: 'High Spike %',
          description: 'Average % gain from open to high',
          target: { column: 'day0_open_high_pct', type: 'column' },
          conditions: [],
          chartType: 'line',
        },
        {
          id: 'gs_d1_low_spike_pct',
          conditionalAgg: 'AVG',
          label: 'Low Spike %',
          description: 'Average % drop from open to low',
          target: { column: 'day0_open_low_pct', type: 'column' },
          conditions: [],
          chartType: 'line',
        },
        {
          id: 'gs_d1_range',
          conditionalAgg: 'AVG',
          label: 'Range',
          description: 'Average intraday range',
          target: { column: 'day0_range', type: 'column' },
          conditions: [],
          chartType: 'line',
        },
        {
          id: 'gs_d1_volume',
          conditionalAgg: 'AVG',
          label: 'Volume',
          description: 'Average session volume',
          target: { column: 'day0_vol', type: 'column' },
          conditions: [],
          chartType: 'bar',
        },
        {
          id: 'gs_d2_gap_up_3',
          conditionalAgg: 'CNT',
          label: 'Gap Up 3%',
          description: 'Day 2 sessions that gapped up 3% or more',
          target: { column: 'day1_close', type: 'column' },
          conditions: [
            {
              left: { column: 'day1_gap_pct' },
              operator: 'gte',
              right: 3,
              rightType: 'value',
              enabled: true,
            },
          ],
          chartType: 'bar',
        },
        {
          id: 'gs_d2_gap_down_3',
          conditionalAgg: 'CNT',
          label: 'Gap Down 3%',
          description: 'Day 2 sessions that gapped down 3% or more',
          target: { column: 'day1_close', type: 'column' },
          conditions: [
            {
              left: { column: 'day1_gap_pct' },
              operator: 'lte',
              right: -3,
              rightType: 'value',
              enabled: true,
            },
          ],
          chartType: 'bar',
        },
        {
          id: 'gs_d2_close_gt_open',
          conditionalAgg: 'CNT',
          label: 'Close > Open',
          description: 'Day 2 sessions that closed above open',
          target: { column: 'day1_close', type: 'column' },
          conditions: [
            {
              left: { column: 'day1_ret_open' },
              operator: 'gt',
              right: 0,
              rightType: 'value',
              enabled: true,
            },
          ],
          chartType: 'bar',
        },
        {
          id: 'gs_d2_close_lt_open',
          conditionalAgg: 'CNT',
          label: 'Close < Open',
          description: 'Day 2 sessions that closed below open',
          target: { column: 'day1_close', type: 'column' },
          conditions: [
            {
              left: { column: 'day1_ret_open' },
              operator: 'lt',
              right: 0,
              rightType: 'value',
              enabled: true,
            },
          ],
          chartType: 'bar',
        },
        {
          id: 'gs_d2_close_gt_prev_close',
          conditionalAgg: 'CNT',
          label: 'Close > Prev. Close',
          description: 'Day 2 sessions that closed above the prior close',
          target: { column: 'day1_close', type: 'column' },
          conditions: [
            {
              left: { column: 'day1_chg' },
              operator: 'gte',
              right: 0,
              rightType: 'value',
              enabled: true,
            },
          ],
          chartType: 'bar',
        },
        {
          id: 'gs_d2_close_lt_prev_close',
          conditionalAgg: 'CNT',
          label: 'Close < Prev. Close',
          description: 'Day 2 sessions that closed below the prior close',
          target: { column: 'day1_close', type: 'column' },
          conditions: [
            {
              left: { column: 'day1_chg' },
              operator: 'lt',
              right: 0,
              rightType: 'value',
              enabled: true,
            },
          ],
          chartType: 'bar',
        },
        {
          id: 'gs_d2_high_spike_pct',
          conditionalAgg: 'AVG',
          label: 'High Spike %',
          description: 'Average % gain from open to high on Day 2',
          target: { column: 'day1_open_high_pct', type: 'column' },
          conditions: [],
          chartType: 'line',
        },
        {
          id: 'gs_d2_low_spike_pct',
          conditionalAgg: 'AVG',
          label: 'Low Spike %',
          description: 'Average % drop from open to low on Day 2',
          target: { column: 'day1_open_low_pct', type: 'column' },
          conditions: [],
          chartType: 'line',
        },
        {
          id: 'gs_d2_range',
          conditionalAgg: 'AVG',
          label: 'Range',
          description: 'Average intraday range on Day 2',
          target: { column: 'day1_range', type: 'column' },
          conditions: [],
          chartType: 'line',
        },
        {
          id: 'gs_d2_volume',
          conditionalAgg: 'AVG',
          label: 'Volume',
          description: 'Average session volume on Day 2',
          target: { column: 'day1_vol', type: 'column' },
          conditions: [],
          chartType: 'bar',
        },
      ],
      groups: [
        {
          label: 'Day 1',
          data: [
            'gs_d1_count',
            'gs_d1_gap',
            'gs_d1_gap_pct',
            'gs_d1_close_gt_open',
            'gs_d1_close_lt_open',
            'gs_d1_hod',
            'gs_d1_lod',
            'gs_d1_high_spike_pct',
            'gs_d1_low_spike_pct',
            'gs_d1_range',
            'gs_d1_volume',
          ],
        },
        {
          label: 'Day 2',
          data: [
            'gs_d2_gap_up_3',
            'gs_d2_gap_down_3',
            'gs_d2_close_gt_open',
            'gs_d2_close_lt_open',
            'gs_d2_close_gt_prev_close',
            'gs_d2_close_lt_prev_close',
            'gs_d2_high_spike_pct',
            'gs_d2_low_spike_pct',
            'gs_d2_range',
            'gs_d2_volume',
          ],
        },
      ],
    },
  },
  {
    id: 'default_agg_11am_gap_stats',
    name: '11AM Gap Stats',
    aggregateProfile: {
      name: '11AM Gap Stats',
      aggregates: [
        {
          id: 'gap11_d1_count',
          conditionalAgg: 'CNT',
          label: 'Count',
          description: 'Number of sessions in the result set',
          target: { column: 'day0_close', type: 'column' },
          conditions: [],
          chartType: 'bar',
        },
        {
          id: 'gap11_d1_pm_high_pct',
          conditionalAgg: 'AVG',
          label: 'PM High %',
          description: 'Average premarket high %',
          target: { column: 'day0_pm_high_pct', type: 'column' },
          conditions: [],
          chartType: 'line',
        },
        {
          id: 'gap11_d1_pm_vol_avg',
          conditionalAgg: 'AVG',
          label: 'PM Vol',
          description: 'Average premarket volume',
          target: { column: 'day0_pm_vol', type: 'column' },
          conditions: [],
          chartType: 'bar',
        },
        {
          id: 'gap11_d1_pm_vol_max',
          conditionalAgg: 'MAX',
          label: 'PM Vol',
          description: 'Maximum premarket volume',
          target: { column: 'day0_pm_vol', type: 'column' },
          conditions: [],
          chartType: 'bar',
        },
        {
          id: 'gap11_d1_gap_pct_avg',
          conditionalAgg: 'AVG',
          label: 'Gap %',
          description: 'Average percentage gap',
          target: { column: 'day0_gap_pct', type: 'column' },
          conditions: [],
          chartType: 'line',
        },
        {
          id: 'gap11_d1_gap_pct_max',
          conditionalAgg: 'MAX',
          label: 'Gap %',
          description: 'Maximum percentage gap',
          target: { column: 'day0_gap_pct', type: 'column' },
          conditions: [],
          chartType: 'line',
        },
        {
          id: 'gap11_d1_change',
          conditionalAgg: 'AVG',
          label: 'Change',
          description: 'Average daily change ($)',
          target: { column: 'day0_chg', type: 'column' },
          conditions: [],
          chartType: 'line',
        },
        {
          id: 'gap11_d1_change_pct',
          conditionalAgg: 'AVG',
          label: 'Change %',
          description: 'Average daily change %',
          target: { column: 'day0_chg_pct', type: 'column' },
          conditions: [],
          chartType: 'line',
        },
        {
          id: 'gap11_d1_hod',
          conditionalAgg: 'AVG',
          label: 'HOD',
          description: 'Average time of the high of day',
          target: { column: 'day0_high_time', type: 'column' },
          conditions: [],
          chartType: 'line',
        },
        {
          id: 'gap11_d1_lod',
          conditionalAgg: 'AVG',
          label: 'LOD',
          description: 'Average time of the low of day',
          target: { column: 'day0_low_time', type: 'column' },
          conditions: [],
          chartType: 'line',
        },
        {
          id: 'gap11_11am_green',
          conditionalAgg: 'CNT',
          label: '11AM Green',
          description: 'Sessions where the 11AM (90min) close was at/above the open',
          target: { column: 'day1_close', type: 'column' },
          conditions: [
            {
              left: { column: 'day0_90min_close' },
              operator: 'gte',
              right: 'day0_open',
              rightType: 'column',
              enabled: true,
            },
          ],
          chartType: 'bar',
        },
        {
          id: 'gap11_11am_red',
          conditionalAgg: 'CNT',
          label: '11AM Red',
          description: 'Sessions where the 11AM (90min) close was below the open',
          target: { column: 'day1_close', type: 'column' },
          conditions: [
            {
              left: { column: 'day0_90min_close' },
              operator: 'lt',
              right: 'day0_open',
              rightType: 'column',
              enabled: true,
            },
          ],
          chartType: 'bar',
        },
        {
          id: 'gap11_11am_spike_pct_avg',
          conditionalAgg: 'AVG',
          label: '11AM Spike %',
          description: 'Average % gain from open to high (Day 2)',
          target: { column: 'day1_open_high_pct', type: 'column' },
          conditions: [],
          chartType: 'line',
        },
        {
          id: 'gap11_11am_spike_pct_max',
          conditionalAgg: 'MAX',
          label: '11AM Spike %',
          description: 'Maximum % gain from open to high by 11AM (90min)',
          target: { column: 'day0_90min_high_pct', type: 'column' },
          conditions: [],
          chartType: 'line',
        },
        {
          id: 'gap11_11am_fade_pct_avg',
          conditionalAgg: 'AVG',
          label: '11AM Fade %',
          description: 'Average % drop from open to low by 11AM (90min)',
          target: { column: 'day0_90min_low_pct', type: 'column' },
          conditions: [],
          chartType: 'line',
        },
        {
          id: 'gap11_11am_fade_pct_min',
          conditionalAgg: 'MIN',
          label: '11AM Fade %',
          description: 'Largest % drop from open to low by 11AM (90min)',
          target: { column: 'day0_90min_low_pct', type: 'column' },
          conditions: [],
          chartType: 'line',
        },
        {
          id: 'gap11_11am_vol',
          conditionalAgg: 'AVG',
          label: '11AM Vol',
          description: 'Average volume by 11AM (90min)',
          target: { column: 'day0_90min_vol', type: 'column' },
          conditions: [],
          chartType: 'bar',
        },
        {
          id: 'gap11_11am_close_pct',
          conditionalAgg: 'AVG',
          label: '11 AM Close %',
          description: 'Average close % at 11AM (90min)',
          target: { column: 'day0_90min_close_pct', type: 'column' },
          conditions: [],
          chartType: 'line',
        },
        {
          id: 'gap11_d2_gap_pct_avg',
          conditionalAgg: 'AVG',
          label: 'D2 Gap %',
          description: 'Average Day 2 gap %',
          target: { column: 'day1_gap_pct', type: 'column' },
          conditions: [],
          chartType: 'line',
        },
        {
          id: 'gap11_d2_gap_pct_med',
          conditionalAgg: 'MED',
          label: 'D2 Gap %',
          description: 'Median Day 2 gap %',
          target: { column: 'day1_gap_pct', type: 'column' },
          conditions: [],
          chartType: 'line',
        },
        {
          id: 'gap11_d2_gap_up_3',
          conditionalAgg: 'CNT',
          label: 'D2 Gap Up 3%',
          description: 'Day 2 sessions that gapped up 3% or more',
          target: { column: null, type: 'column' },
          conditions: [
            {
              left: { column: 'day1_gap_pct' },
              operator: 'gte',
              right: 3,
              rightType: 'value',
              enabled: true,
            },
          ],
          chartType: 'bar',
        },
        {
          id: 'gap11_d2_gap_down_3',
          conditionalAgg: 'CNT',
          label: 'D2 Gap Down 3%',
          description: 'Day 2 sessions that gapped down 3% or more',
          target: { column: null, type: 'column' },
          conditions: [
            {
              left: { column: 'day1_gap_pct' },
              operator: 'lte',
              right: -3,
              rightType: 'value',
              enabled: true,
            },
          ],
          chartType: 'bar',
        },
        {
          id: 'gap11_d2_chg_open_green',
          conditionalAgg: 'CNT',
          label: 'D2 Chg From Open Green',
          description: 'Day 2 sessions where change from open was positive',
          target: { column: null, type: 'column' },
          conditions: [
            {
              left: { column: 'day1_ret_open' },
              operator: 'gt',
              right: 0,
              rightType: 'value',
              enabled: true,
            },
          ],
          chartType: 'bar',
        },
        {
          id: 'gap11_d2_chg_open_red',
          conditionalAgg: 'CNT',
          label: 'D2 Chg From Open Red',
          description: 'Day 2 sessions where change from open was negative',
          target: { column: null, type: 'column' },
          conditions: [
            {
              left: { column: 'day1_ret_open' },
              operator: 'lt',
              right: 0,
              rightType: 'value',
              enabled: true,
            },
          ],
          chartType: 'bar',
        },
        {
          id: 'gap11_d2_close_green',
          conditionalAgg: 'CNT',
          label: 'D2 Close Green',
          description: 'Day 2 sessions that closed above the prior close',
          target: { column: null, type: 'column' },
          conditions: [
            {
              left: { column: 'day1_chg' },
              operator: 'gt',
              right: 0,
              rightType: 'value',
              enabled: true,
            },
          ],
          chartType: 'bar',
        },
        {
          id: 'gap11_d2_close_red',
          conditionalAgg: 'CNT',
          label: 'D2 Close Red',
          description: 'Day 2 sessions that closed below the prior close',
          target: { column: null, type: 'column' },
          conditions: [
            {
              left: { column: 'day1_chg' },
              operator: 'lt',
              right: 0,
              rightType: 'value',
              enabled: true,
            },
          ],
          chartType: 'bar',
        },
        {
          id: 'gap11_d2_high_spike_pct',
          conditionalAgg: 'AVG',
          label: 'D2 High Spike %',
          description: 'Average % gain from open to high on Day 2',
          target: { column: 'day1_open_high_pct', type: 'column' },
          conditions: [],
          chartType: 'line',
        },
        {
          id: 'gap11_d2_low_spike_pct',
          conditionalAgg: 'AVG',
          label: 'D2 Low Spike %',
          description: 'Average % drop from open to low on Day 2',
          target: { column: 'day1_open_low_pct', type: 'column' },
          conditions: [],
          chartType: 'line',
        },
        {
          id: 'gap11_d2_volume',
          conditionalAgg: 'AVG',
          label: 'D2 Volume',
          description: 'Average session volume on Day 2',
          target: { column: 'day1_vol', type: 'column' },
          conditions: [],
          chartType: 'bar',
        },
      ],
      groups: [
        {
          label: 'Day 1',
          data: [
            'gap11_d1_count',
            'gap11_d1_pm_high_pct',
            'gap11_d1_pm_vol_avg',
            'gap11_d1_pm_vol_max',
            'gap11_d1_gap_pct_avg',
            'gap11_d1_gap_pct_max',
            'gap11_d1_change',
            'gap11_d1_change_pct',
            'gap11_d1_hod',
            'gap11_d1_lod',
          ],
        },
        {
          label: '11AM Metric',
          data: [
            'gap11_11am_green',
            'gap11_11am_red',
            'gap11_11am_spike_pct_avg',
            'gap11_11am_spike_pct_max',
            'gap11_11am_fade_pct_avg',
            'gap11_11am_fade_pct_min',
            'gap11_11am_vol',
            'gap11_11am_close_pct',
          ],
        },
        {
          label: 'Group',
          data: [
            'gap11_d2_gap_pct_avg',
            'gap11_d2_gap_pct_med',
            'gap11_d2_gap_up_3',
            'gap11_d2_gap_down_3',
            'gap11_d2_chg_open_green',
            'gap11_d2_chg_open_red',
            'gap11_d2_close_green',
            'gap11_d2_close_red',
            'gap11_d2_high_spike_pct',
            'gap11_d2_low_spike_pct',
            'gap11_d2_volume',
          ],
        },
      ],
    },
  },
  {
    // Legacy `predef__stats-11am` (history/defaultProfiles.js:532), transcribed 2026-09-18.
    id: 'default_agg_11am_stats',
    name: '11AM Stats',
    aggregateProfile: {
      name: '11AM Stats',
      aggregates: [
        {
          id: 's11_count',
          conditionalAgg: 'CNT',
          label: 'Count',
          description: '',
          target: { column: 'day0_close', type: 'column' },
          conditions: [],
        },
        {
          id: 's11_green',
          conditionalAgg: 'CNT',
          label: '11AM Green',
          description: '',
          target: { column: 'day0_range', type: 'column' },
          conditions: [
            {
              left: { column: 'day0_90min_close' },
              operator: 'gte',
              right: 'day0_open',
              rightType: 'column',
              enabled: true,
            },
          ],
        },
        {
          id: 's11_red',
          conditionalAgg: 'CNT',
          label: '11AM Red',
          description: '',
          target: { column: 'day0_vol', type: 'column' },
          conditions: [
            {
              left: { column: 'day0_90min_close' },
              operator: 'lt',
              right: 'day0_open',
              rightType: 'column',
              enabled: true,
            },
          ],
        },
        {
          id: 's11_spike_avg',
          conditionalAgg: 'AVG',
          label: '11AM Spike %',
          description: '',
          target: { column: 'day0_90min_high_pct', type: 'column' },
          conditions: [],
        },
        {
          id: 's11_spike_max',
          conditionalAgg: 'MAX',
          label: '11AM Spike %',
          description: '',
          target: { column: 'day0_90min_high_pct', type: 'column' },
          conditions: [],
        },
        {
          id: 's11_fade_avg',
          conditionalAgg: 'AVG',
          label: '11AM Fade %',
          description: '',
          target: { column: 'day0_90min_low_pct', type: 'column' },
          conditions: [],
        },
        {
          id: 's11_fade_min',
          conditionalAgg: 'MIN',
          label: '11AM Fade %',
          description: '',
          target: { column: 'day0_90min_low_pct', type: 'column' },
          conditions: [],
        },
        {
          id: 's11_volume',
          conditionalAgg: 'AVG',
          label: '11AM Volume',
          description: '',
          target: { column: 'day0_90min_vol', type: 'column' },
          conditions: [],
        },
        {
          id: 's11_close_pct',
          conditionalAgg: 'AVG',
          label: '11 AM Close %',
          description: '',
          target: { column: 'day0_90min_close_pct', type: 'column' },
          conditions: [],
        },
        {
          id: 's11_eod_pm_high',
          conditionalAgg: 'AVG',
          label: 'PM High %',
          description: '',
          target: { column: 'day0_pm_high_pct', type: 'column' },
          conditions: [],
        },
        {
          id: 's11_eod_pm_vol',
          conditionalAgg: 'AVG',
          label: 'PM Vol',
          description: '',
          target: { column: 'day0_pm_vol', type: 'column' },
          conditions: [],
        },
        {
          id: 's11_eod_gap',
          conditionalAgg: 'AVG',
          label: 'Gap %',
          description: '',
          target: { column: 'day0_gap_pct', type: 'column' },
          conditions: [],
        },
        {
          id: 's11_eod_chg',
          conditionalAgg: 'AVG',
          label: 'Change %',
          description: '',
          target: { column: 'day0_chg_pct', type: 'column' },
          conditions: [],
        },
        {
          id: 's11_eod_spike',
          conditionalAgg: 'AVG',
          label: 'Spike %',
          description: '',
          target: { column: 'day0_close_high_pct', type: 'column' },
          conditions: [],
        },
        {
          id: 's11_eod_low',
          conditionalAgg: 'AVG',
          label: 'Low %',
          description: '',
          target: { column: 'day0_close_low_pct', type: 'column' },
          conditions: [],
        },
        {
          id: 's11_eod_hod',
          conditionalAgg: 'AVG',
          label: 'HOD Time',
          description: '',
          target: { column: 'day0_high_time', type: 'column' },
          conditions: [],
        },
        {
          id: 's11_eod_lod',
          conditionalAgg: 'AVG',
          label: 'LOD Time',
          description: '',
          target: { column: 'day0_low_time', type: 'column' },
          conditions: [],
        },
        {
          id: 's11_eod_volume',
          conditionalAgg: 'AVG',
          label: 'Volume',
          description: '',
          target: { column: 'day0_vol', type: 'column' },
          conditions: [],
        },
      ],
      groups: [
        {
          label: 'D[1] 11AM Metrics',
          data: [
            's11_count',
            's11_green',
            's11_red',
            's11_spike_avg',
            's11_spike_max',
            's11_fade_avg',
            's11_fade_min',
            's11_volume',
            's11_close_pct',
          ],
        },
        {
          label: 'D[1] EOD Stats',
          data: [
            's11_eod_pm_high',
            's11_eod_pm_vol',
            's11_eod_gap',
            's11_eod_chg',
            's11_eod_spike',
            's11_eod_low',
            's11_eod_hod',
            's11_eod_lod',
            's11_eod_volume',
          ],
        },
      ],
    },
  },
]

// ─── Color Profile Preset ───

export const ColorProfilePresetSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    rules: z.array(ColorRuleSchema),
    createdAt: z.string().optional(),
    updatedAt: z.string().optional(),
  })
  .passthrough()

export type ColorProfilePreset = z.infer<typeof ColorProfilePresetSchema>
